<section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
  <app-ui-section-header
    label="Assinatura"
    help="Plano atual, status, limites do plano e faturas da mensalidade."
  />

<div class="mt-6 grid gap-6">
  <section class="grid gap-4">
    @if (loading()) {
    <app-ui-skeleton-grid [rows]="2" [rowHeight]="72" />
  } @else if (error()) {
    <app-ui-empty-state icon="heroCreditCard" title="Erro ao carregar" [description]="error()!">
      <button appButton variant="secondary" actions (click)="load()">Tentar novamente</button>
    </app-ui-empty-state>
  } @else if (plan(); as p) {
    <article appUiPanelCard class="grid gap-4">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="flex items-center gap-3">
          <span class="grid size-10 place-items-center rounded-full bg-accent-soft text-accent-strong">
            <ng-icon name="heroCreditCard" size="20" aria-hidden="true" />
          </span>
          <div>
            <p class="text-base font-semibold text-ink">
              {{ p.name }}
              <span appBadge [tone]="p.statusTone" class="ml-1">{{ p.statusLabel }}</span>
            </p>
            <p class="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-muted">
              @if (p.priceLabel) {
                <span class="font-medium text-ink">{{ p.priceLabel }}</span>
              }
              <span>Ciclo {{ p.cycleLabel }}@if (p.renewalLabel) { · {{ p.renewalLabel }} }</span>
              @if (creditMode()) {
                <span>· {{ includedCreditLabel() }} de crédito incluso / ciclo</span>
              }
            </p>
          </div>
        </div>
        @if (canManage()) {
          @if (canReactivate()) {
            <button appButton variant="secondary" size="sm" type="button" [disabled]="actionPending()" (click)="reactivateSubscription()">
              <ng-icon name="heroArrowUturnLeft" size="16" aria-hidden="true" /> Reativar
            </button>
          } @else if (canCancel()) {
            <button appButton variant="danger" size="sm" type="button" [disabled]="actionPending()" (click)="cancelSubscription()">
              Cancelar
            </button>
          }
        }
      </div>
      <div>
        <a appButton variant="primary" size="sm" routerLink="planos">Ver planos e preços</a>
      </div>
    </article>

    @if (cancelScheduled()) {
      <div appUiPanelCard class="border-tomato/35 bg-tomato/5">
        <p class="text-sm font-medium text-ink">Cancelamento agendado</p>
        <p appUiHint class="mt-1">
          Sua assinatura mantém o acesso ao plano {{ p.name }} até {{ p.periodEndLabel ?? 'o fim do ciclo' }},
          sem cobrança nova e sem reembolso do período atual. Use "Reativar" para desfazer.
        </p>
      </div>
    } @else if (downgradeScheduled(); as d) {
      <div appUiPanelCard class="border-accent/35 bg-accent-soft/40">
        <p class="text-sm font-medium text-ink">Troca de plano agendada</p>
        <p appUiHint class="mt-1">
          No fim do ciclo atual@if (d.atLabel) { ({{ d.atLabel }}) }, sua assinatura passa para o plano
          <span class="font-medium text-ink">{{ d.planName }}</span>. Use "Reativar" para cancelar a troca e manter o plano atual.
        </p>
      </div>
    }

    @if (creditMode()) {
      <div appUiPanelCard>
        <p appUiHint>Crédito incluso</p>
        <p class="text-2xl font-semibold text-ink">
          {{ includedCreditLabel() }} <span class="text-base font-normal text-muted">/ ciclo</span>
        </p>
        <p appUiHint class="mt-1">
          Usado em qualquer métrica pela tarifa de cada uma; reseta a cada renovação. Acompanhe o consumo em
          <a class="font-medium text-accent-strong underline" routerLink="/configuracoes/uso-e-quotas">Consumo</a>.
        </p>
      </div>
    }

    @if (capacityRows().length) {
      <div class="grid gap-3">
        <p class="text-sm font-medium text-ink">Limites do plano</p>
        <div class="grid gap-4">
          @for (row of capacityRows(); track row.resource) {
            <div>
              <div class="flex flex-wrap items-center justify-between gap-2">
                <span class="text-sm font-medium capitalize text-ink">{{ row.label }}</span>
                <span class="flex items-center gap-2">
                  @if (row.statusLabel) {
                    <span appBadge [tone]="row.tone" size="xs">{{ row.statusLabel }}</span>
                  }
                  <span appUiHint>{{ row.unlimited ? row.usageLabel + ' · ilimitado' : row.usageLabel }}</span>
                </span>
              </div>
              @if (!row.unlimited) {
                <app-ui-attainment-bar
                  class="mt-1 block"
                  [realized]="row.realized"
                  [target]="row.target"
                  [hasForecast]="false"
                />
              }
              @if (row.showUpgrade && canManage()) {
                <a appButton variant="secondary" size="sm" class="mt-2 inline-flex" routerLink="planos">Ver planos</a>
              }
            </div>
          }
        </div>
        <p appUiHint>
          Limites de capacidade do seu plano — quantos usuários, agentes e canais você pode ter. Ao encostar no
          limite, troque de plano para liberar mais.
        </p>
      </div>
    }
  } @else {
    <app-ui-empty-state
      icon="heroCreditCard"
      title="Sem plano ativo"
      description="Sua organização ainda não tem uma assinatura associada. O consumo continua sendo medido e aparece na tela Consumo."
    />
  }
  </section>

  <section class="grid gap-4 border-t border-line pt-5">
    <app-ui-section-header label="Faturas" help="Histórico das faturas da mensalidade do plano." />
    @if (cardLabel(); as card) {
      <p appUiHint>
        Cobrado no cartão {{ card }} — gerenciar em
        <a class="font-medium text-accent-strong underline" routerLink="/configuracoes/cobranca">Cobrança</a>.
      </p>
    }
    <app-billing-invoices-section *appIfPermission="'billing:invoice:read'" />
  </section>
</div>
</section>
